import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import React, { useRef, useMemo, useEffect, useState } from 'react';
import { RiCircleLine, RiShapeLine } from 'react-icons/ri';
import { useDispatch, useStore } from 'react-redux';

import styles from './Fence.module.scss';

import actions from '@/actions';
import AccordionCaret from '@/components/ui/AccordionCaret';
import FieldRow from '@/components/ui/FieldRow';
import { FieldNumber, FieldText } from '@/components/ui/kit/Field';
import MorePanel from '@/components/ui/MorePanel';
import EventEmitter from '@/libs/EventEmitter';
import { NotifierService as notifier } from '@/libs/Notifier';
import { getPolygonArea, getCircleArea } from '@/utils/MapUtils';

const cx = classNames.bind(styles);

const Fence = ({ index, data: fence }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [isOpenBody, setIsOpenBody] = useState(true);
  const domRefs = useRef({
    name: useRef(),
    latitude: useRef(),
    longitude: useRef(),
    radius: useRef(),
    points: useRef(),
    area: useRef(),
  }).current;

  const Icon = useMemo(() => {
    switch (fence.type) {
      case 'Circle':
        return RiCircleLine;
      case 'Polygon':
        return RiShapeLine;
      default:
        return;
    }
  }, []);

  useEffect(() => {
    domRefs.name.current.value = fence.data.name;

    if (fence.type === 'Circle') {
      domRefs.latitude.current.value = fence.data.center.lat.toFixed(7);
      domRefs.longitude.current.value = fence.data.center.lng.toFixed(7);
      domRefs.radius.current.value = Math.round(fence.data.radius);
      domRefs.area.current.value = getCircleArea(fence.data.radius).toFixed(6);
    } else if (fence.type === 'Polygon') {
      domRefs.points.current.value = fence.data.boundary.length;
      domRefs.area.current.value = getPolygonArea(fence.data.boundary).toFixed(6);
    }
  }, [fence.data]);

  useMountEffect(() => {
    const toggle = (nextIsOpenBody) => {
      if (nextIsOpenBody === undefined) {
        setIsOpenBody((prev) => !prev);
      } else {
        setIsOpenBody(nextIsOpenBody);
      }
    };

    const subscribeToken = EventEmitter.subscribe(`fence/${fence.id}/toggle`, toggle);

    return () => {
      EventEmitter.unsubscribe(subscribeToken);
    };
  });

  const toggleBody = () => {
    setIsOpenBody(!isOpenBody);
  };

  const remove = () => {
    if (store.getState().map3d.isShow) {
      notifier.error('Fences cannot be deleted in 3D map.');
      return;
    }

    dispatch(actions.mission.mavlink.removeFence(index));
  };

  const updateName = (e) => {
    dispatch(actions.mission.mavlink.editFence(index, { name: e.target.value }));
  };

  return (
    <div className={cx('container')}>
      <div className={cx('header')} onClick={toggleBody}>
        <div className={cx('left')}>
          <div className={cx('icon')}>
            <Icon size={20} color="white" />
          </div>
          <div className={cx('type')}>{fence.type}</div>
        </div>
        <div className={cx('right')}>
          <MorePanel data={[{ name: 'Remove', callback: remove }]} />
          <AccordionCaret up={isOpenBody} size={32} />
        </div>
      </div>
      <div className={cx(['body', { open: isOpenBody }])}>
        <div className={cx('inner')}>
          <FieldRow label="Name">
            <FieldText ref={domRefs.name} name="name" maxLength={24} onBlur={updateName} />
          </FieldRow>
          {fence.type === 'Circle' && (
            <>
              <FieldRow label="Latitude">
                <FieldNumber ref={domRefs.latitude} name="latitude" readOnly />
              </FieldRow>
              <FieldRow label="Longitude">
                <FieldNumber ref={domRefs.longitude} name="longitude" readOnly />
              </FieldRow>
              <FieldRow label="Radius" unit="m">
                <FieldNumber ref={domRefs.radius} name="radius" readOnly />
              </FieldRow>
            </>
          )}
          {fence.type === 'Polygon' && (
            <FieldRow label="Points">
              <FieldNumber ref={domRefs.points} name="points" readOnly />
            </FieldRow>
          )}
          <FieldRow label="Area" unit="㎢">
            <FieldNumber ref={domRefs.area} name="area" readOnly />
          </FieldRow>
        </div>
      </div>
    </div>
  );
};

export default Fence;
